import {
  BasketColumn,
  BasketColumnKeys,
  OrderType,
  Total,
} from 'graphql-api/baskets-api-interfaces';

import { ManualDiscountBasketColumn } from '../manual-discount-basket-column';
import {
  getManualDiscount,
  getSimulatedTotal,
  isOverwrittenDiscount,
} from '../shared';

import { addConfirmedValuesForKey } from './add-confirmed-values-to-key';
import { totalToDiscount } from './total-to-discount';

export function makeTotalQuantityColumn(totals: Total): BasketColumn {
  return {
    key: BasketColumnKeys.Quantity,
    value: totals.requested.quantity,
    ...addConfirmedValuesForKey(totals, BasketColumnKeys.Quantity),
  };
}

/**
 * Create subtotal and totals columns (for footer and basket lines) (first three levels of the basket)
 */
export const makeBasketLineColumns = ({
  total,
  isManualDiscountApplied,
  orderType,
}: {
  total: Total;
  isManualDiscountApplied: boolean;
  orderType?: OrderType;
}): BasketColumn[] => {
  const totals = total;

  if (!totals) {
    return [];
  }

  const manualDiscount = getManualDiscount({ total, isManualDiscountApplied });

  const totalsToDiscountWithoutRrp = totalToDiscount(totals, false);
  const useTotalToDiscountForManualDiscountColumn =
    manualDiscount?.percentage === undefined;

  const manualDiscountBasketColumn: ManualDiscountBasketColumn = {
    key: BasketColumnKeys.ManualDiscount,
    discount: useTotalToDiscountForManualDiscountColumn
      ? totalsToDiscountWithoutRrp
      : manualDiscount,
    currency: totals.requested.currency,
    isDefault: useTotalToDiscountForManualDiscountColumn,
  };

  const isCloseOutProduct = Boolean(total.products?.[0]?.isCloseOutProduct);

  const overwritten = isOverwrittenDiscount(
    isManualDiscountApplied,
    isCloseOutProduct,
    orderType,
  );

  return [
    makeTotalQuantityColumn(totals),
    {
      key: BasketColumnKeys.Discount,
      discount: totalsToDiscountWithoutRrp,
    },
    {
      key: BasketColumnKeys.DiscountPercentage,
      discount: totalsToDiscountWithoutRrp,
      currency: totals.requested.currency,
      overwritten,
    },
    {
      key: BasketColumnKeys.DiscountRrp,
      discount: totalToDiscount(totals, true),
    },
    {
      key: BasketColumnKeys.Total,
      value: totals.confirmed?.net,
      currency: totals.requested.currency,
      overwritten,
    },
    {
      key: BasketColumnKeys.Value,
      value: totals.requested.displayedwsp,
      ...addConfirmedValuesForKey(
        totals,
        BasketColumnKeys.Wsp,
        BasketColumnKeys.Net,
      ),
      currency: totals.requested.currency,
    },
    {
      key: BasketColumnKeys.Gross,
      value: totals.confirmed?.wsp,
      currency: totals.requested.currency,
    },
    {
      key: BasketColumnKeys.ValueRrp,
      value: totals.requested.rrp,
      ...addConfirmedValuesForKey(
        totals,
        BasketColumnKeys.Rrp,
        BasketColumnKeys.Net,
      ),
      currency: totals.requested.currencyRrp,
    },
    {
      key: BasketColumnKeys.ValueNet,
      value: totals.requested.net,
      ...addConfirmedValuesForKey(totals, BasketColumnKeys.Net),
      currency: totals.requested.currency,
    },
    manualDiscountBasketColumn,
    {
      key: BasketColumnKeys.SimulatedTotal,
      value: getSimulatedTotal(totals, isManualDiscountApplied),
      currency: totals.requested.currency,
    },
  ];
};
